import { useEffect, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Check, PauseCircle } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ChapterOpening } from "@/components/book/ChapterOpening"
import { StatusChip } from "@/pages/App/Coach/Books/components/bookStatus"
import { FlagPlace } from "@/pages/App/Coach/Books/components/BrandCheckCard"
import { useFlagHighlights } from "@/hooks/use-flag-highlights"

// One chapter of a Kingdom Factors book, read only, for KF admin: the
// approved version, else the latest. The Brand check's flags show beside it,
// highlighted in the text; #flag-<id> in the link goes to that flag's words.
export default function BookChapter({ currentUser, book, chapter, version, flags = [] }) {
  const page = useRef(null)
  const locate = useFlagHighlights(page, flags, { watch: Boolean(version) })
  const { places, show } = locate

  useEffect(() => {
    const id = Number(window.location.hash.match(/^#flag-(\d+)$/)?.[1])
    if (id && places?.[id]) show(id)
  }, [places === null])

  const decide = (flag, action) => router.post(`/app/admin/brand_flags/${flag.id}/${action}`, {}, { preserveScroll: true })

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title={`${chapter.leaderName} - ${book.title} - Kingdom Factor`} />
      <div className={`mx-auto space-y-4 ${flags.length > 0 ? "max-w-6xl" : "max-w-3xl"}`}>
        <Link href={`/app/admin/books/${book.id}`} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900">
          <ArrowLeft className="h-4 w-4" /> {book.title}
        </Link>
        <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-600">
          <StatusChip status={chapter.status} />
          {version && <span>Version {version.number}{version.approved ? ", approved by the coach" : ", not approved yet"}</span>}
        </div>
        <div className={flags.length > 0 ? "grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]" : ""}>
          {version ? (
            <article className="kf-book-page rounded-lg border bg-white px-5 py-8 shadow-sm sm:px-10">
              <ChapterOpening {...chapter} title={version.title} subtitle={version.subtitle} />
              {/* The Brand check reads the sections, not the opening. */}
              <div ref={page}>
                {version.sections.map((section) => (
                  <section key={section.key}>
                    <h2>{section.heading}</h2>
                    {/* ChapterTemplate's HTML, sanitized on every write. */}
                    <div dangerouslySetInnerHTML={{ __html: section.body }} />
                  </section>
                ))}
              </div>
            </article>
          ) : (
            <p className="rounded-md border bg-white p-6 text-sm text-zinc-600">This chapter has no draft yet.</p>
          )}

          {flags.length > 0 && (
            <aside>
              <Card className="xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
                <CardContent className="space-y-3 text-sm">
                  <b className="block">Brand check flags</b>
                  {flags.map((flag) => (
                    <div key={flag.id} className={`space-y-1.5 rounded-md border p-3 ${flag.severity === "serious" ? "border-red-200 bg-red-50/50" : "border-amber-200 bg-amber-50/50"}`}>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${flag.severity === "serious" ? "bg-red-600 text-white" : "bg-amber-200 text-amber-900"}`}>
                          {flag.severity === "serious" ? "Serious" : "Note"}
                        </span>
                        <span className="text-zinc-700">{flag.topic}</span>
                      </div>
                      <p className="italic text-zinc-900">“{flag.quote}”</p>
                      {places && <FlagPlace place={places[flag.id]} onShow={() => show(flag.id)} showText="Show in chapter" />}
                      <p className="text-xs text-zinc-700"><b>Why:</b> {flag.reason}</p>
                      {flag.suggestion && <p className="text-xs text-zinc-700"><b>Suggested fix:</b> {flag.suggestion}</p>}
                      <p className="text-xs text-zinc-600">
                        {flag.cleared ? "Cleared" : flag.held ? "On hold" : flag.kept ? "Kept by the coach" : "The coach has not decided"}
                      </p>
                      {flag.severity === "serious" && !flag.cleared && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button size="sm" onClick={() => decide(flag, "clear")}><Check className="h-4 w-4" /> Clear the flag</Button>
                          {!flag.held && (
                            <Button size="sm" variant="outline" onClick={() => decide(flag, "hold")}><PauseCircle className="h-4 w-4" /> Keep on hold</Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </aside>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
